/**
 * UserAvatar — canonical way to render a rapper's face across the app.
 *
 * Rendering priority:
 * 1. Nano-Banana-generated portrait PNG (`portrait` prop)
 * 2. Static AI opponent portrait (`portrait` prop pointing to /characters/*)
 * 3. Initials fallback in a stage-colored square — NEVER an emoji
 *
 * The emoji avatar picker from the OG MVP ( etc.) is retired.
 * Every rapper now looks like an actual character, either from the AI roster
 * or the user's own Character Studio generation.
 */
import React from "react";
import { apiPortraitUrl } from "@/auth";

// Deterministic accent color per rapper name so initials don't all look
// identical — gives the leaderboard/feed cards visual variety even before
// portraits exist for every user.
const ACCENTS = [
 "bg-yt-red text-white",
 "bg-yt-yellow text-black",
 "bg-yt-judge text-black",
 "bg-white text-black",
];
function accent(name) {
 const s = (name || "?").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
 return ACCENTS[s % ACCENTS.length];
}
function initials(name) {
 if (!name) return "?";
 const parts = name.trim().split(/\s+/).filter(Boolean);
 if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
 return name.slice(0, 2).toUpperCase();
}

export default function UserAvatar({
 portrait,
 name,
 size = "md", // sm | md | lg | xl | fill
 className = "",
 testid,
}) {
 const dims = {
 sm: "w-8 h-8 text-xs",
 md: "w-12 h-12 text-sm",
 lg: "w-20 h-20 text-lg",
 xl: "w-32 h-32 text-3xl",
 fill: "w-full h-full text-3xl",
 }[size] || "w-12 h-12 text-sm";

 const src = portrait ? (apiPortraitUrl(portrait) || portrait) : null;

 if (src) {
 return (
 <img
 data-testid={testid}
 src={src}
 alt={name || "rapper"}
 className={`${dims} object-cover object-top border-2 border-yt-border ${className}`}
 draggable={false}
 />
 );
 }
 return (
 <div
 data-testid={testid}
 className={`${dims} ${accent(name)} font-display flex items-center justify-center border-2 border-yt-border ${className}`}
 >
 {initials(name)}
 </div>
 );
}
